import cx from 'classnames'
import AccessDeniedView from 'components/AccessDeniedView'
import ContentError from 'components/ContentError'
import ContentLoading from 'components/ContentLoading'
import { Sandwich, SandwichBun, SandwichContent } from 'components/Sandwich'
import { SplitView, SplitViewMain, SplitViewSide } from 'components/SplitView'
import { Forbidden } from 'fejl'
import { useDocumentTitle } from 'hooks/document-title'
import { useElementSize } from 'hooks/element-size'
import { useActivation } from 'hooks/mobx-hooks'
import { useObservableCallback } from 'hooks/rx-hooks'
import { Cancelled } from 'infra/cancellation'
import link from 'infra/link'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import DeleteJobDialog from 'jobs/views/DeleteJobDialog'
import RenameJobDialog from 'jobs/views/RenameJobDialog'
import { observer } from 'mobx-react'
import * as React from 'react'
import { RouteChildrenProps, useHistory } from 'react-router'
import { merge } from 'rxjs'
import { map } from 'rxjs/operators'
import { Id } from 'types/definitions'
import JobCreationRootViewModel from '../../JobCreationRootViewModel'
import JobCreationViewModel from '../../JobCreationViewModel'
import Header from './Header'
import styles from './JobCreation.sass'
import JobCreationFormView from './JobCreationFormView'
import Outline from './Outline'
import CopyJobDialog from 'jobs/views/CopyJobDialog'

interface JobCreationRootProps
  extends RouteChildrenProps<{ jobId?: string | undefined }> {
  vm: JobCreationRootViewModel
}

export default observer(function JobCreationRoot({
  vm,
  match,
  location,
}: JobCreationRootProps) {
  useDocumentTitle('Loading...')

  const history = useHistory()
  useObservableCallback(
    () =>
      // Replace the URL of History with...
      merge(
        // draft editor when we have a Job instance
        vm.jobCreated$.pipe(map((job) => link.editDraftJob(Id.assert(job.id)))),
        // job management if the draft is deleted
        vm.jobDeleted$.pipe(map(() => link.jobs())),
        // job details if the job that was loaded is not a draft
        vm.notADraft$.pipe(map((job) => link.job(Id.assert(job.id))))
      ),
    (url) => history.replace(url),
    [vm, history]
  )

  // An existing draft job to edit, if any.
  const jobId = match?.params?.jobId
  const edit = React.useCallback(() => {
    vm.edit(jobId, PrefilledDraftInput.parse(location.search))
      // Ignore cancellation errors.
      .catch(Cancelled.ignore())
  }, [vm, Id(jobId), location.search])

  // Activates the VM on load, deactivates on unload.
  useActivation(vm)

  // Used for switching between jobs.
  React.useEffect(edit, [edit])

  return vm.edit.match({
    pending: () => <ContentLoading />,
    rejected: (err) =>
      err instanceof Forbidden ? (
        <Denied />
      ) : (
        <ContentError large error={err} onRetryClick={edit} />
      ),
    resolved: (editor) =>
      vm.canViewEditor ? <JobCreation vm={editor} /> : <Denied />,
  })
})

interface JobCreationProps {
  vm: JobCreationViewModel
}

const JobCreation = observer(function JobCreation({ vm }: JobCreationProps) {
  useDocumentTitle(vm.viewTitle)
  const rootRef = React.useRef(null)
  // TODO: Make mobile friendly as per Kels' design.
  // Currently don't have the time for that so will
  // just hide the outline for now.
  const rootSize = useElementSize(rootRef)
  const shouldShowOutline = rootSize.width >= 900
  return (
    <div className={cx(styles.root)} ref={rootRef} data-test="draft-editor">
      <SplitView>
        {shouldShowOutline && (
          <SplitViewSide>
            <Outline title={vm.viewTitle} vm={vm.outline} />
          </SplitViewSide>
        )}

        <SplitViewMain>
          <Sandwich stretch>
            <SandwichBun>
              <Header vm={vm} />
            </SandwichBun>
            <SandwichContent>
              <JobCreationFormView vm={vm} />
            </SandwichContent>
          </Sandwich>
        </SplitViewMain>
      </SplitView>
      <DeleteJobDialog vm={vm.deleteModal} />
      <RenameJobDialog vm={vm.renameModal} />
      <CopyJobDialog vm={vm.copyModal} />
    </div>
  )
})

const Denied = () => {
  useDocumentTitle('Access Denied')
  return <AccessDeniedView />
}
